/**
 * SupplierPicker — controlled combobox that selects a Supplier doc by
 * its id (supplier-{guid}). Surfaces an inline "+ Create new supplier"
 * affordance that opens CreateSupplierModal; on create-success, refetches
 * the list and auto-selects the new supplier.
 *
 * Props:
 *   value:        string  — supplier-{guid} currently selected, or ''
 *   onChange:     (id: string) => void
 *   callerEmail:  string  — passed to useSuppliers + CreateSupplierModal
 *   placeholder?: string
 *   className?:   string  — applied to the <input> (parity with ComboBox)
 *
 * Display contract:
 *   - When picker is closed and value matches a known supplier:
 *       input shows that supplier's supplier_name.
 *   - When picker is closed and value is set but no match in the loaded
 *       list (e.g., suppliers still loading): input shows value as-is.
 *   - When picker is open: input is the user's typeahead query.
 *
 * Behaviour:
 *   - Focus or chevron click opens the dropdown.
 *   - Typing filters by case-insensitive substring on supplier_name.
 *   - Click a row -> onChange(row.id), input updates to row.supplier_name,
 *     dropdown closes.
 *   - Click "+ Create new supplier" -> opens CreateSupplierModal with
 *     initialName = the current typed query.
 *   - Outside-click / Escape closes the dropdown and reverts the input
 *     to the currently selected supplier's name.
 *
 * Layout:
 *   - Dropdown is rendered via position: absolute (same as ComboBox).
 *     If the table cell ever clips it, fall back to a portal — not
 *     implemented here because ComboBox shares this assumption and works
 *     in the same row.
 *   - z-index: 50 matches ComboBox (above page content, below modal).
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSuppliers } from '../../../shared/hooks/useSuppliers';
import CreateSupplierModal from './CreateSupplierModal';
import './SupplierPicker.css';

let _listSeq = 0;
function nextListId() { return `sp-list-${++_listSeq}`; }

export default function SupplierPicker({
  value,
  onChange,
  callerEmail,
  placeholder = 'Choose a supplier',
  className = '',
}) {
  const { suppliers, loading, error, refetch } = useSuppliers(callerEmail);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useRef(nextListId()).current;

  // Resolve the displayed name for the currently selected id. Fall back
  // to the raw value when suppliers haven't loaded yet so a pre-set
  // value doesn't render as blank during the load window.
  const selectedName = useMemo(() => {
    if (!value) return '';
    const found = suppliers.find(
      (s) => s.id === value || s.supplier_id === value
    );
    return found ? (found.supplier_name || '') : value;
  }, [value, suppliers]);

  // Keep the input text in sync with the selection while the picker is
  // closed AND the create-modal is not open. While either is true the user
  // is mid-interaction with their typed query: the picker dropdown is in
  // typeahead mode, or the create-modal was opened from the dropdown and
  // is reading the typed query as initialName. Resetting query while the
  // modal is open would (a) flip the picker input behind the modal back
  // to the previously-selected supplier_name and (b) cause the modal's
  // [isOpen, initialName] effect to re-init supplierName to that
  // reverted value — the bug fixed by Sitting 8 Phase 2B-fix.
  useEffect(() => {
    if (!open && !createOpen) setQuery(selectedName);
  }, [selectedName, open, createOpen]);

  // Outside-click closes the dropdown without changing selection.
  useEffect(() => {
    if (!open) return undefined;
    function handleDocMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [open]);

  // Case-insensitive substring filter on supplier_name.
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) =>
      (s.supplier_name || '').toLowerCase().includes(q)
    );
  }, [suppliers, query]);

  const handleSelect = useCallback((s) => {
    onChange(s.id || s.supplier_id);
    setOpen(false);
  }, [onChange]);

  const handleOpenCreate = useCallback(() => {
    setCreateOpen(true);
    setOpen(false);
  }, []);

  const handleCreated = useCallback(async (newSupplier) => {
    await refetch();
    onChange(newSupplier.id || newSupplier.supplier_id);
    setCreateOpen(false);
  }, [refetch, onChange]);

  // preventDefault on mousedown inside the menu keeps the input focused
  // so option/button onClick fires before the dropdown can close from a
  // blur race. Same trick ComboBox uses.
  function handleMenuMouseDown(e) {
    e.preventDefault();
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  }

  const inputClassName = `sp-input${className ? ` ${className}` : ''}`;

  return (
    <div className="sp-wrap" ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listId}
      />
      <button
        type="button"
        className={`sp-chevron${open ? ' open' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setOpen((prev) => !prev);
          inputRef.current?.focus();
        }}
        aria-label={open ? 'Close suppliers' : 'Open suppliers'}
        tabIndex={-1}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.75"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul id={listId} className="sp-menu" role="listbox" onMouseDown={handleMenuMouseDown}>
          {loading && suppliers.length === 0 && (
            <li className="sp-status">Loading suppliers…</li>
          )}
          {error && (
            <li className="sp-status sp-status--error">
              Could not load suppliers: {error.message || String(error)}
            </li>
          )}
          {!loading && !error && filtered.length === 0 && (
            <li className="sp-status">No matches</li>
          )}
          {filtered.map((s) => {
            const id = s.id || s.supplier_id;
            return (
              <li
                key={id}
                className={`sp-option${value === id ? ' sp-option--selected' : ''}`}
                role="option"
                aria-selected={value === id}
                onClick={() => handleSelect(s)}
              >
                {s.supplier_name || '(unnamed)'}
              </li>
            );
          })}
          <li className="sp-create-row">
            <button
              type="button"
              className="sp-create-btn"
              onClick={handleOpenCreate}
            >
              + Create new supplier
            </button>
          </li>
        </ul>
      )}

      <CreateSupplierModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        initialName={query}
        callerEmail={callerEmail}
      />
    </div>
  );
}
